import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const Layout: React.FC = () => {
  const { user, userRole, logout } = useAuth();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-blue-700' : 'hover:bg-blue-600';
  };

  const isDropdownActive = (paths: string[]) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  const toggleDropdown = (name: string) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const getRoleBadge = () => {
    const badges = {
      learner: { label: 'Learner', color: 'bg-green-600' },
      instructor: { label: 'Assessor', color: 'bg-blue-600' },
      administrator: { label: 'Admin', color: 'bg-purple-600' },
    };
    return badges[userRole] || badges.learner;
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-blue-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center space-x-1">
              <div className="flex-shrink-0 mr-4">
                <h1 className="text-xl font-bold whitespace-nowrap">Apprenticeship Tracker</h1>
              </div>
              <div className="flex space-x-1">
                {/* Dashboard */}
                <Link
                  to="/"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${isActive('/')}`}
                >
                  Dashboard
                </Link>

                {/* Management Dropdown - Administrators only */}
                {userRole === 'administrator' && (
                  <div className="relative">
                    <button
                      onClick={() => toggleDropdown('management')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition flex items-center whitespace-nowrap ${
                        isDropdownActive(['/admin', '/instructors', '/courses', '/standards', '/ilr']) ? 'bg-blue-700' : 'hover:bg-blue-600'
                      }`}
                    >
                      Management
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdown === 'management' && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Settings
                        </Link>
                        <Link
                          to="/instructors"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Assessors
                        </Link>
                        <Link
                          to="/courses"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Courses
                        </Link>
                        <Link
                          to="/standards"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Standards
                        </Link>
                        <Link
                          to="/ilr"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          ILR Generation
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* Apprentices Dropdown - Instructors and Administrators */}
                {(userRole === 'instructor' || userRole === 'administrator') && (
                  <div className="relative">
                    <button
                      onClick={() => toggleDropdown('apprentices')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition flex items-center whitespace-nowrap ${
                        isDropdownActive(['/apprentices', '/tracking/otj/new']) ? 'bg-blue-700' : 'hover:bg-blue-600'
                      }`}
                    >
                      Apprentices
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdown === 'apprentices' && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <Link
                          to="/apprentices"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          View All
                        </Link>
                        <Link
                          to="/tracking/otj/new"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Log OTJ Hours
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* Assessment Dropdown - Instructors and Administrators */}
                {(userRole === 'instructor' || userRole === 'administrator') && (
                  <div className="relative">
                    <button
                      onClick={() => toggleDropdown('assessment')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition flex items-center whitespace-nowrap ${
                        isDropdownActive(['/tracking', '/progress-reviews']) ? 'bg-blue-700' : 'hover:bg-blue-600'
                      }`}
                    >
                      Assessment
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdown === 'assessment' && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <Link
                          to="/tracking"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          OTJ Tracking
                        </Link>
                        <Link
                          to="/progress-reviews"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50"
                          onClick={() => setOpenDropdown(null)}
                        >
                          Progress Reviews
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* My Hours - Learners only */}
                {userRole === 'learner' && (
                  <Link
                    to="/my-otj"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${isActive('/my-otj')}`}
                  >
                    My Hours
                  </Link>
                )}

                {/* Log Hours - Learners only */}
                {userRole === 'learner' && (
                  <Link
                    to="/tracking/otj/new"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${isActive('/tracking/otj/new')}`}
                  >
                    Log Hours
                  </Link>
                )}

                {/* Standards - Only for Instructors and Learners (Admins access via Management) */}
                {userRole !== 'administrator' && (
                  <Link
                    to="/standards"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${isActive('/standards')}`}
                  >
                    Standards
                  </Link>
                )}
              </div>
            </div>

            {/* Right side - User info and Logout */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${roleBadge.color} whitespace-nowrap`}>
                  {roleBadge.label}
                </span>
                {user && (
                  <div className="text-sm hidden lg:block">
                    <div className="font-medium truncate max-w-xs">{user.user_name || 'User'}</div>
                  </div>
                )}
              </div>
              <button
                onClick={logout}
                className="px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-600 transition whitespace-nowrap"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Click outside to close dropdowns */}
      {openDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpenDropdown(null)}
        />
      )}

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
};
